/**
 * STAFFING GRID — the pure week/month maths behind the Manpower spreadsheet view.
 *
 * Kept separate from the component and unit-tested because every bug in a calendar is a DATE bug, and
 * date bugs are invisible until the wrong person shows up on the wrong day. Two traps this module exists
 * to avoid:
 *
 *  1. `new Date('2026-10-08')` parses as UTC midnight, which in a negative-offset timezone is the
 *     PREVIOUS day locally. Every date string here is therefore parsed into its local calendar parts
 *     explicitly, never via the Date string constructor.
 *  2. Shifts are stored in UTC. Bucketing one into a day column needs the shift's LOCAL date, which is
 *     not the first 10 characters of its ISO string -- a 23:00 CDT shift is stored as the next day in UTC.
 */

/** A day column: its local calendar date plus display bits. */
export interface GridDay {
  date: string;          // YYYY-MM-DD, local calendar
  dayOfWeek: number;     // 1 = Monday .. 7 = Sunday
  label: string;         // 'Mon'
  dayOfMonth: number;
  inMonth: boolean;      // month view only: is this cell in the displayed month
}

const DOW_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Parse YYYY-MM-DD into a LOCAL Date at midnight. Never uses the string constructor (see header). */
export const parseLocalDate = (date: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const dt = new Date(y, mo - 1, d);
  // Reject a rolled-over date like 2026-02-30, which JS would silently turn into March 2nd.
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null;
  return dt;
};

/** Format a local Date back to YYYY-MM-DD using LOCAL parts, not toISOString. */
export const formatLocalDate = (dt: Date): string =>
  `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;

/** The local calendar date a UTC instant falls on, for the viewer. */
export const localDateOfUtc = (utcIso?: string): string | null => {
  if (!utcIso) return null;
  const dt = new Date(utcIso);
  if (!Number.isFinite(dt.getTime())) return null;
  return formatLocalDate(dt);
};

/** Monday of the week containing `date`. Weeks run Mon..Sun per Braden. */
export const mondayOf = (date: string): string | null => {
  const dt = parseLocalDate(date);
  if (!dt) return null;
  const js = dt.getDay();                 // 0 = Sunday
  const back = js === 0 ? 6 : js - 1;     // Sunday belongs to the week that STARTED six days earlier
  dt.setDate(dt.getDate() - back);
  return formatLocalDate(dt);
};

/** The seven Mon..Sun columns of the week containing `date`. */
export const weekDays = (date: string): GridDay[] => {
  const monday = mondayOf(date);
  if (!monday) return [];
  const base = parseLocalDate(monday)!;
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() + i);
    return {
      date: formatLocalDate(d),
      dayOfWeek: i + 1,
      label: DOW_LABELS[i],
      dayOfMonth: d.getDate(),
      inMonth: true,
    };
  });
};

/**
 * A month as calendar rows, Monday-first, padded with leading/trailing days so every row has 7 cells.
 * Always whole weeks, so the grid never has a ragged edge; `inMonth` marks the padding.
 */
export const monthGrid = (date: string): GridDay[][] => {
  const dt = parseLocalDate(date);
  if (!dt) return [];
  const year = dt.getFullYear(), month = dt.getMonth();
  const first = new Date(year, month, 1);
  const startMonday = parseLocalDate(mondayOf(formatLocalDate(first))!)!;
  const last = new Date(year, month + 1, 0);
  const rows: GridDay[][] = [];
  const cursor = new Date(startMonday.getFullYear(), startMonday.getMonth(), startMonday.getDate());
  // Guard the loop on a cell count as well as the date, so a pathological input cannot spin forever.
  for (let week = 0; week < 6; week++) {
    const row: GridDay[] = [];
    for (let i = 0; i < 7; i++) {
      row.push({
        date: formatLocalDate(cursor),
        dayOfWeek: i + 1,
        label: DOW_LABELS[i],
        dayOfMonth: cursor.getDate(),
        inMonth: cursor.getMonth() === month,
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    rows.push(row);
    if (cursor > last && cursor.getMonth() !== month) break;
  }
  return rows;
};

export interface GridShift {
  employeeId: string;
  employeeType?: string;
  startTimeUtc?: string;
  endTimeUtc?: string;
  order?: number | null;
}

/** employeeId -> local date -> that person's shifts on that day. */
export const shiftsByEmployeeDay = (shifts: GridShift[]): Map<string, Map<string, GridShift[]>> => {
  const out = new Map<string, Map<string, GridShift[]>>();
  for (const s of shifts) {
    const day = localDateOfUtc(s.startTimeUtc);
    if (!day) continue;                       // a shift with no usable start cannot be placed on a column
    let byDay = out.get(s.employeeId);
    if (!byDay) { byDay = new Map(); out.set(s.employeeId, byDay); }
    const list = byDay.get(day);
    if (list) list.push(s); else byDay.set(day, [s]);
  }
  return out;
};

export type CellKind = 'off' | 'pecking' | 'shift';

export interface GridCell {
  kind: CellKind;
  /** '#3' for pecking, '0700-1500' for shift work, '' when off. */
  text: string;
  order?: number;
  /** Shifts beyond the one displayed. A provider can legitimately hold two rows on one day. */
  extra: number;
  /** The shift the text came from, so the caller can colour the cell the way the board would. */
  shift?: GridShift;
}

const hhmm = (utc?: string): string => {
  if (!utc) return '';
  const d = new Date(utc);
  if (!Number.isFinite(d.getTime())) return '';
  return `${String(d.getHours()).padStart(2, '0')}${String(d.getMinutes()).padStart(2, '0')}`;
};

/**
 * What one cell shows. A PECKING shift wins over a shift-work one when somebody holds both, matching how
 * ManpowerCoverageCard dedupes and what the bullpen displays -- the pecking number is the more specific
 * fact. Any additional shifts are counted in `extra` rather than dropped silently, because a person
 * genuinely can hold two rows on one day (a carried-in night shift plus a day shift).
 */
export const cellFor = (shifts: GridShift[] | undefined): GridCell => {
  if (!shifts || shifts.length === 0) return { kind: 'off', text: '', extra: 0 };
  const pecking = shifts.filter(s => s.order !== undefined && s.order !== null);
  const chosen = pecking.length > 0
    ? pecking.reduce((a, b) => ((a.order ?? 0) <= (b.order ?? 0) ? a : b))
    : [...shifts].sort((a, b) => (hhmm(a.startTimeUtc) || '9999').localeCompare(hhmm(b.startTimeUtc) || '9999'))[0];
  const extra = shifts.length - 1;
  if (chosen.order !== undefined && chosen.order !== null) {
    return { kind: 'pecking', text: `#${chosen.order}`, order: chosen.order, extra, shift: chosen };
  }
  const start = hhmm(chosen.startTimeUtc), end = hhmm(chosen.endTimeUtc);
  return { kind: 'shift', text: start && end ? `${start}-${end}` : (start || end || '?'), extra, shift: chosen };
};

/** Per-day totals for the month calendar: how many of each type are rostered. */
export const dayTotals = (
  byEmployeeDay: Map<string, Map<string, GridShift[]>>,
  typeOf: (employeeId: string) => string | undefined,
  date: string,
): { doctors: number; crnas: number } => {
  let doctors = 0, crnas = 0;
  for (const [employeeId, byDay] of byEmployeeDay) {
    if (!byDay.has(date)) continue;
    const t = typeOf(employeeId);
    if (t === 'doctor') doctors++;
    else if (t === 'nurse') crnas++;
  }
  return { doctors, crnas };
};

/**
 * What a typed cell value means. ONE input accepts both shapes, because that is how a spreadsheet is
 * actually used -- you tab in and type, you do not pick a mode first.
 *
 *   "5" / "#5"            -> set pecking order 5
 *   "0700-1500" / "7:00-15:00" -> set the shift window
 *   ""                    -> clear (remove the pecking order, or delete a shift-work shift)
 *
 * A LONE time like "0700" is deliberately INVALID rather than guessed at: it could plausibly mean a
 * start time or a 4-digit pecking number, and silently choosing one would write the wrong thing.
 */
export type CellInput =
  | { kind: 'clear' }
  | { kind: 'pecking'; order: number }
  | { kind: 'window'; startHHMM: string; endHHMM: string }
  | { kind: 'invalid'; reason: string };

export const parseCellInput = (raw: string): CellInput => {
  const s = (raw ?? '').trim();
  if (s === '') return { kind: 'clear' };

  const peck = /^#?(\d{1,3})$/.exec(s);
  if (peck) {
    const n = Number(peck[1]);
    return n >= 1
      ? { kind: 'pecking', order: n }
      : { kind: 'invalid', reason: 'Pecking order starts at 1' };
  }

  // Accept 0700-1500, 07:00-15:00, 7:00-15:00, and en/em dashes (a paste from a spreadsheet).
  const win = /^(\d{1,2}):?(\d{2})\s*[-\u2013\u2014]\s*(\d{1,2}):?(\d{2})$/.exec(s);
  if (win) {
    const h1 = Number(win[1]), m1 = Number(win[2]), h2 = Number(win[3]), m2 = Number(win[4]);
    if (h1 > 23 || h2 > 23 || m1 > 59 || m2 > 59) {
      return { kind: 'invalid', reason: 'Hours are 0-23 and minutes 0-59' };
    }
    const pad = (h: number, m: number) => `${String(h).padStart(2, '0')}${String(m).padStart(2, '0')}`;
    return { kind: 'window', startHHMM: pad(h1, m1), endHHMM: pad(h2, m2) };
  }

  return { kind: 'invalid', reason: 'Type a pecking number (5) or a window (0700-1500)' };
};

/**
 * Local date + HHMM -> a UTC instant. Built from local calendar parts so the browser applies the right
 * offset (and the right DST rule for that date) rather than assuming a fixed one.
 *
 * An end at or before the start is treated as the NEXT DAY, so an overnight shift typed as 1900-0300
 * means what a human means by it. The backend rejects end <= start, so without this every night shift
 * would be refused.
 */
export const utcFromLocal = (date: string, hhmm: string, nextDay = false): string | null => {
  const base = parseLocalDate(date);
  if (!base || !/^\d{4}$/.test(hhmm)) return null;
  const h = Number(hhmm.slice(0, 2)), m = Number(hhmm.slice(2));
  if (h > 23 || m > 59) return null;
  const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() + (nextDay ? 1 : 0), h, m, 0, 0);
  return d.toISOString();
};

/** True when a typed window crosses midnight, so the end belongs to the following day. */
export const windowCrossesMidnight = (startHHMM: string, endHHMM: string): boolean =>
  Number(endHHMM) <= Number(startHHMM);

/** The single column of a Day view. inMonth is true so it is never dimmed like month padding. */
export const singleDay = (date: string): GridDay[] => {
  const dt = parseLocalDate(date);
  if (!dt) return [];
  const js = dt.getDay();
  return [{
    date,
    dayOfWeek: js === 0 ? 7 : js,
    label: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][js],
    dayOfMonth: dt.getDate(),
    inMonth: true,
  }];
};

export interface WindowCount { label: string; startMin: number; endMin: number; count: number }

export interface SectionTally {
  /** Per column, so a footer row can sit under each day heading. */
  perDate: { date: string; rostered: number; pecking: number }[];
  /** Shift-window buckets. Pecking-order people are NOT in these -- see below. */
  windows: WindowCount[];
  pecking: number;
  rostered: number;
  off: number;
}

const hhmmToMin = (hhmm: string): number =>
  Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(2, 4));

/**
 * Totals for one section (the doctors, or the CRNAs) across the columns on screen.
 *
 * PECKING-ORDER PEOPLE ARE COUNTED SEPARATELY, not inside a window bucket. They do have times, but the
 * bullpen already splits shift-work-grouped-by-window from a numbered pecking group, and putting them
 * in both would inflate the window counts and the pecking count against the same person.
 *
 * Windows sort by END then START, matching groupByWindow on the CRNA tab, so a 1900 finish sorts after
 * a 1700 one regardless of when it started.
 *
 * `rostered`/`off` count PERSON-DAYS, not people: across a week one person contributes up to seven. The
 * caller labels the scope, because "0630-1500: 60" means sixty shifts over a week, not sixty people.
 */
export const sectionTally = (
  employeeIds: string[],
  byEmployeeDay: Map<string, Map<string, GridShift[]>>,
  dates: string[],
): SectionTally => {
  const perDate = dates.map(date => ({ date, rostered: 0, pecking: 0 }));
  const byLabel = new Map<string, WindowCount>();
  let pecking = 0, rostered = 0, off = 0;

  dates.forEach((date, i) => {
    for (const id of employeeIds) {
      const cell = cellFor(byEmployeeDay.get(id)?.get(date));
      if (cell.kind === 'off') { off++; continue; }
      rostered++; perDate[i].rostered++;
      if (cell.kind === 'pecking') { pecking++; perDate[i].pecking++; continue; }
      const found = byLabel.get(cell.text);
      if (found) { found.count++; continue; }
      // A malformed label (a half-populated shift) sorts last rather than throwing off the order.
      const m = /^(\d{4})-(\d{4})$/.exec(cell.text);
      byLabel.set(cell.text, {
        label: cell.text,
        startMin: m ? hhmmToMin(m[1]) : Number.MAX_SAFE_INTEGER,
        endMin: m ? hhmmToMin(m[2]) : Number.MAX_SAFE_INTEGER,
        count: 1,
      });
    }
  });

  const windows = [...byLabel.values()].sort((a, b) => a.endMin - b.endMin || a.startMin - b.startMin);
  return { perDate, windows, pecking, rostered, off };
};
